import { Component, input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ListItem,
  SourceEnum,
} from '../../../../../shared/store/typeahead/types';
import { Observable } from 'rxjs';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DocumentTypeService } from '../../../../../shared/store/document-type/document-type.service';
import { SubHeaderService } from '../../../../../shared/store/subheader/subheader.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { filterTrue } from '../../../../../shared/pipe/rxjs/operators';
import { take } from 'rxjs/operators';
import { DocumentTypeExtendedModel } from '../../../../../shared/store/document-type/types';
import {
  ModalComponent,
  ModalComponentKey,
} from 'src/app/shared/service/modal-component';

@UntilDestroy()
@ModalComponent(ModalComponentKey.SelectClaimTypeDocumentTypeEdit)
@Component({
  selector: 'app-select-claim-type-document-type-edit-content',
  standalone: false,
  templateUrl: './select-claim-type-document-type-edit-content.component.html',
  styleUrl: './select-claim-type-document-type-edit-content.component.scss',
})
export class SelectClaimTypeDocumentTypeEditContentComponent
  implements OnInit, OnDestroy
{
  readonly source = input<SourceEnum>(SourceEnum.Component);
  readonly modalRandomIdentifier = input<number>();
  /** Documents already linked to the claim type, passed in by the opener. */
  readonly selectedDocumentTypes = input<DocumentTypeExtendedModel[]>([]);

  documentTypeListItem$: Observable<ListItem[]>;
  form: FormGroup;

  constructor(
    private readonly documentTypeService: DocumentTypeService,
    private readonly formBuilder: FormBuilder,
    private readonly subHeaderService: SubHeaderService,
    protected readonly activeModalService: NgbActiveModal,
  ) {
    this.documentTypeListItem$ = this.documentTypeService.getListItem.value$;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      documentTypes: this.formBuilder.array([]),
    });

    this.documentTypeService.getListItem.call({
      filter: 'recordType=0', //do not include system document
      pageSize: 100,
      orderBy: '',
    });

    this.documentTypeListItem$
      .pipe(filterTrue(), take(1))
      .subscribe((documentTypes) => {
        documentTypes.forEach((documentType) => {
          const documentTypeForm = this.formBuilder.group({
            id: [documentType.id],
            name: [documentType.name],
            checked: [false, [Validators.required]],
            mandatory: [true],
          });
          this.syncMandatoryState(documentTypeForm);
          this.documentTypes.push(documentTypeForm);
        });

        this.selectedDocumentTypes().forEach((documentType) =>
          this.addDocumentType(documentType),
        );
      });

    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'claimTypeDocumentTypesUpdate') {
          this.onSubmit();
        }
        this.subHeaderService.setTriggerAction('');
      });
  }

  onSubmit() {
    const controls = this.documentTypes.controls as FormGroup[];

    // getRawValue keeps the mandatory flag of rows whose checkbox is disabled.
    const documentTypes: DocumentTypeExtendedModel[] = controls
      .map((x: FormGroup) => x.getRawValue())
      .filter((x) => x.checked == true)
      .map((x) => ({
        id: x.id,
        name: x.name,
        description: '',
        mandatory: x.mandatory,
      }));

    // The selection is the modal result: it resolves the opener's modalRef.result promise.
    this.activeModalService.close(documentTypes);
  }

  get documentTypes() {
    return this.form.controls['documentTypes'] as FormArray;
  }

  checkDocumentType(documentTypeId: number | null) {
    let controls = this.documentTypes.controls as FormControl[];
    const index = controls.findIndex(
      (x: FormControl) => x.value.id == documentTypeId,
    );
    if (index === -1) return;

    const group = this.documentTypes.controls[index] as FormGroup;
    const value = group.controls['checked'].value;

    group.controls['checked'].patchValue(!value);
    this.syncMandatoryState(group);
  }

  /** The mandatory flag only makes sense on a selected document. */
  isChecked(index: number): boolean {
    const group = this.documentTypes.controls[index] as FormGroup | undefined;
    return !!group?.controls['checked'].value;
  }

  /**
   * Greys out the mandatory checkbox of an unselected row. Driven through the reactive form API:
   * the checkbox value accessor owns the DOM disabled property, so an [attr.disabled] binding on
   * the input gets wiped by setDisabledState() on the first render.
   */
  private syncMandatoryState(group: FormGroup) {
    const mandatory = group.controls['mandatory'];

    if (group.controls['checked'].value) {
      mandatory.enable({ emitEvent: false });
    } else {
      mandatory.disable({ emitEvent: false });
    }
  }

  updateMandatoryValue(documentTypeId: number | null) {
    let controls = this.documentTypes.controls as FormControl[];
    const index = controls.findIndex(
      (x: FormControl) => x.value.id == documentTypeId,
    );
    if (index === -1 || !this.isChecked(index)) return;

    const value = (this.documentTypes.controls[index] as FormGroup).controls[
      'mandatory'
    ].value;

    (this.documentTypes.controls[index] as FormGroup).controls[
      'mandatory'
    ].patchValue(!value);
  }

  addDocumentType(documentType: DocumentTypeExtendedModel) {
    let controls = this.documentTypes.controls as FormControl[];
    const index = controls.findIndex(
      (x: FormControl) => x.value.id == documentType?.id,
    );
    if (index === -1) return;

    const group = this.documentTypes.controls[index] as FormGroup;

    group.controls['checked'].patchValue(true);
    group.controls['mandatory'].patchValue(documentType?.mandatory ?? true);
    this.syncMandatoryState(group);
  }

  ngOnDestroy(): void {
    this.documentTypeService.getListItem.reset();
  }
}
